import { twMerge } from 'tailwind-merge';

import { SUPPORT_REASON_BECOME_MENTOR, SupportForm } from '@/components/supportForm';
import { Button, ButtonText } from '@/components/ui/button';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { useSupportPrompt } from '@/features/support-contact';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { segmentClient } from '@/util/analytics';
import { getProfileNameAndInitials } from '@/util/getProfileNameAndInitials';

type BecomeMentorCardProps = {
  className?: string;
};

/**
 * "Become a mentor" action for Fellows Corner — the give-back invite.
 *
 * Rendered as a secondary action beside the mentor-lineup heading, where its
 * recruitment intent has context without competing with the fellow-focused
 * page hero or inquiry workflow. It retains button semantics because it opens
 * a form, while the link treatment reflects its supporting hierarchy.
 *
 * There's no dedicated become-a-mentor flow yet, so it routes interest through
 * the existing Contact Support form, preselected to the "Become a Mentor" reason
 * and prefilled from the signed-in user. Replace with a purpose-built flow once
 * the mentor-onboarding decision is made with Kate.
 */
export function BecomeMentorCard({ className }: BecomeMentorCardProps) {
  const { currentUser } = useAuthStatus();

  const supportPrompt = useSupportPrompt({
    source: 'become_mentor',
    draft: 'I’d like to become a Fellows Corner mentor.',
  });

  const name = currentUser ? getProfileNameAndInitials(currentUser).name : '';

  return (
    <>
      <Button
        variant="link"
        action="primary"
        size="sm"
        accessibilityLabel="Become a mentor"
        onPress={() => {
          segmentClient.track(ANALYTICS_EVENTS.FELLOWS_BECOME_MENTOR_CLICKED)?.catch(() => {});

          supportPrompt.open();
        }}
        className={twMerge(
          'shrink-0 bg-transparent web:cursor-pointer data-[focus-visible=true]:web:ring-primary-600',
          className
        )}>
        <ButtonText className="font-semibold text-primary-700 data-[hover=true]:text-primary-800 data-[active=true]:text-primary-900">
          Become a mentor
        </ButtonText>
      </Button>

      {supportPrompt.isFormOpen ? (
        <SupportForm
          isOpen={supportPrompt.isFormOpen}
          onClose={supportPrompt.closeForm}
          npi={currentUser?.npiNumber ?? ''}
          institution={currentUser?.institution ?? ''}
          state={currentUser?.state ?? ''}
          firstName={currentUser?.firstName ?? ''}
          lastName={currentUser?.lastName ?? ''}
          identifiesAsDoctor={currentUser?.identifiesAsDoctor}
          initialValues={{
            reason: SUPPORT_REASON_BECOME_MENTOR,
            name,
            email: currentUser?.email ?? '',
            message: "I'd like to become a Fellows Corner mentor.",
          }}
        />
      ) : null}
    </>
  );
}
